import React, { useEffect, useState, useRef } from "react";
import useMediaQuery from "../../hooks/useMediaQuery";
import { useNavigate, useParams } from "react-router";
import useTicket from "../../hooks/useTicket";
import { format, parseISO } from 'date-fns';
import { it } from 'date-fns/locale';
import CardLoadingPage from "../../components/CardLoadingPage/CardLoadingPage";
import useTicketMessaggi from "../../hooks/useTicketMessaggi";
import CustomPagination from "../../components/CustomPagination/CustomPagination";
import CustomButton from "../../components/CustomButton/CustomButton";
import ComebackArrow from "../../components/ComebackArrow/ComebackArrow";
import CustomModal from "../../components/CustomModal/CustomModal";
import IconWithTooltip from "../../components/IconWithTooltip/IconWithTooltip";
import { STATO_TICKET } from "../../model/statoTicket";
import Badge from 'react-bootstrap/Badge';
import TicketManagerForm from "../../components/Forms/TicketManagerForm/TicketManagerForm";
import { useSelector } from "react-redux";
import { ROLES } from "../../model/roles";

export default function TicketPage() {
  const {
    getTicketsByParams,
    loadingStateTicket,
    modalStateTicket,
    setModalStateTicket,
    updateTicket,
    changeTicketStatus
  } = useTicket();

  const {
    createTicketMessaggio,
    deleteTicketMessaggio,
    loadingStateTicketMessaggio,
    updateTicketMessaggio,
  } = useTicketMessaggi();

  const isWideScreen = useMediaQuery("(min-width: 1200px)");
  const navigate = useNavigate();
  const urlParams = useParams();
  const [ticket, setTicket] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [totalPages, setTotalPages] = useState(1);
  const [selectedPage, setSelectedPage] = useState(1);
  const [ticketMessaggi, setTicketMessaggi] = useState([]);
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);

  const [editingMessageId, setEditingMessageId] = useState(null);

  const [deletingMessageId, setDeletingMessageId] = useState(null);

  const [ticketReplyText, setTicketReplyText] = useState('');
  const [commentReplyText, setCommentReplyText] = useState('');
  const [formUpdateTicket, setFormUpdateTicket] = useState(null);
  const userLoggedIn = useSelector((state) => state.user.userData);
  const isTicketClosed = ticket?.stato === STATO_TICKET.CHIUSO;
  const isMobile = useMediaQuery('(max-width: 767px)');
  const isTablet = useMediaQuery('(min-width: 768px) and (max-width: 991px)');

  useEffect(() => {
    fetchTicket();
  }, []);

  const fetchTicket = async () => {
    const params = {
      filtri: {
        id_ticket: +urlParams.idTicket,
      },
    };
    const ticket = await getTicketsByParams(params);
    const updatedTicket = ticket?.data[0];
    setTicket(updatedTicket);
    setFormUpdateTicket(updatedTicket);
    setTicketMessaggi(updatedTicket?.messaggi || []);
    updatePagination(updatedTicket?.messaggi || []);
    return updatedTicket;
  };

  const isUserAuthorizedToEdit = (idUtente) => {
    return userLoggedIn.id_utente === idUtente || userLoggedIn.role === ROLES.ADMIN || userLoggedIn.role === ROLES.DEVELOPER;
  }

  const updatePagination = (messages) => {
    const mainMessages = messages.filter(m => m.id_ticket_messaggio_padre === null);
    const countPagination = Math.ceil(mainMessages.length / 10);
    setTotalPages(countPagination);
    setSelectedPage(prev => Math.min(prev, countPagination || 1));
  };

  const handlePageChange = (page) => {
    setSelectedPage(page);
  };

  const renderPaginatedMessages = () => {
    const mainMessages = ticketMessaggi.filter(m => m.id_ticket_messaggio_padre === null);
    const startIndex = (selectedPage - 1) * 10;
    const endIndex = startIndex + 10;
    const paginatedMessages = mainMessages.slice(startIndex, endIndex);
    return paginatedMessages.map(message => renderMessage(message));
  };

  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return '';
    const date = parseISO(dateTimeString);
    return format(date, "d MMMM yyyy 'alle' HH:mm", { locale: it });
  };

  const handleReply = async (parentId = null) => {
    const replyText = parentId ? commentReplyText : ticketReplyText;
    if (!replyText.trim()) return;

    const ticketMessaggio = {
      id_ticket: ticket.id_ticket,
      testo: replyText.trim(),
    };

    if (parentId) {
      ticketMessaggio.id_ticket_messaggio_padre = parentId;
    }

    await createTicketMessaggio(ticketMessaggio);
    if (parentId) {
      setCommentReplyText('');
      setReplyingTo(null);
    } else {
      setTicketReplyText('');
      setShowReplyForm(false);
    }
    fetchTicket();

  };

  const handleEdit = (messageId) => {
    setEditingMessageId(messageId);
    const messageToEdit = ticketMessaggi.find(m => m.id_ticket_messaggio === messageId);
    setReplyText(messageToEdit.testo);
    setReplyingTo(messageId);
  };

  const handleUpdate = async (messageId) => {
    if (!replyText.trim()) return;

    await updateTicketMessaggio({ 
      id_ticket_messaggio: messageId, 
      testo: replyText.trim() 
    });
    setEditingMessageId(null);
    setReplyingTo(null);
    setReplyText('');
    fetchTicket();
  };

  const handleDelete = (messageId) => {
    setDeletingMessageId(messageId);
  };

  const confirmDelete = async (messageId) => {
    const deleteMessage = {id_ticket_messaggio: messageId};
    await deleteTicketMessaggio(deleteMessage);
    const updatedTicket = await fetchTicket();
    
    const mainMessages = updatedTicket.messaggi.filter(m => m.id_ticket_messaggio_padre === null);
    const newTotalPages = Math.ceil(mainMessages.length / 10);
    if (selectedPage > newTotalPages) {
      setSelectedPage(Math.max(newTotalPages, 1));
    }
    setDeletingMessageId(null);
  };

  const cancelDelete = () => {
    setDeletingMessageId(null);
  };

  const renderMessage = (message, depth = 0) => {
    const parentMessage = ticketMessaggi.find(m => m.id_ticket_messaggio === message.id_ticket_messaggio_padre);
    const isEditing = editingMessageId === message.id_ticket_messaggio;
    const replies = ticketMessaggi.filter(m => m.id_ticket_messaggio_padre === message.id_ticket_messaggio);
    const canEditOrDelete = isUserAuthorizedToEdit(message.utente.id_utente);
    
    return (
      <div 
        key={message.id_ticket_messaggio} 
        className="card mb-3 shadow-sm" 
        style={{ 
          marginLeft: `${depth * 20}px`,
          borderLeft: depth > 0 ? '3px solid orange' : 'none',
          borderRight: "none"
        }}
      >
        <div className="card-body">
          {depth > 0 && parentMessage && (
            <div className="reply-indicator mb-2 text-muted">
              <i className="bi bi-arrow-return-right me-2"></i>
              In risposta al messaggio di {parentMessage.utente.nome} {parentMessage.utente.cognome}
            </div>
          )}
          <div className="d-flex justify-content-between align-items-center mb-2">
            <div>
              <strong>{message.utente.nome} {message.utente.cognome}</strong>
              <span className="badge bg-primary ms-2">{message.utente.tipo_utente}</span>
            </div>
            <small className="text-muted">
              {formatDateTime(message.data_inserimento)}
            </small>
          </div>
          {!isEditing && <p className="card-text">{message.testo}</p>}
          {!isEditing && (
            <div className="mt-2 d-flex justify-content-start align-items-center">
              {deletingMessageId === message.id_ticket_messaggio ? (
                <>
                  <span className="me-2">Sei sicuro di voler eliminare il messaggio?</span>
                  <IconWithTooltip 
                    icon="bi-check"
                    text="Conferma"
                    onClick={() => confirmDelete(message.id_ticket_messaggio)}
                    variant="success"
                    disabled={loadingStateTicketMessaggio.isLoadingTicketMessaggioDelete || loadingStateTicket.isLoadingTicketsByParams}
                  />
                  <IconWithTooltip 
                    icon="bi-x"
                    text="Annulla"
                    onClick={cancelDelete}
                    variant="danger"
                    disabled={loadingStateTicketMessaggio.isLoadingTicketMessaggioDelete || loadingStateTicket.isLoadingTicketsByParams}
                  />
                </>
              ) : (
                <>
                {
                  !isTicketClosed && (
                    <>
                      <IconWithTooltip 
                        icon="bi-reply"
                        text="Rispondi"
                        onClick={() => setReplyingTo(message.id_ticket_messaggio)}
                      />
                      {canEditOrDelete && (
                        <>
                          <IconWithTooltip 
                            icon="bi-pencil"
                            text="Modifica"
                            onClick={() => handleEdit(message.id_ticket_messaggio)}
                          />
                          <IconWithTooltip 
                            icon="bi-trash"
                            text="Elimina"
                            onClick={() => handleDelete(message.id_ticket_messaggio)}
                          />
                        </>
                      )}
                    </>
                  )
                }
                </>
              )}
            </div>
          )}
          {(replyingTo === message.id_ticket_messaggio || isEditing) && (
            <div className="mt-3">
              <textarea 
                className="form-control mb-2" 
                rows="2" 
                value={isEditing ? replyText : commentReplyText}
                onChange={(e) => isEditing ? setReplyText(e.target.value) : setCommentReplyText(e.target.value)}
                placeholder={isEditing ? "Modifica il tuo messaggio..." : "Scrivi la tua risposta..."}
              />
              <CustomButton 
                onClick={() => isEditing ? handleUpdate(message.id_ticket_messaggio) : handleReply(message.id_ticket_messaggio)}
                isLoading={loadingStateTicketMessaggio.isLoadingTicketMessaggioCreating || loadingStateTicketMessaggio.isLoadingTicketMessaggioUpdate}
                label={isEditing ? 'Aggiorna messaggio' : 'Invia risposta'}
                className="btn-sm"
              />
              <button 
                className="btn btn-secondary btn-sm ms-2" 
                onClick={() => {
                  setReplyingTo(null);
                  setEditingMessageId(null);
                  setCommentReplyText('');
                  setReplyText('');
                }}
              >
                Annulla
              </button>
            </div>
          )}
        </div>
        {replies.length > 0 && (
          <div className="nested-replies">
            {replies.map(reply => renderMessage(reply, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  const handleViewCloseTicket = () => {
    setModalStateTicket({...modalStateTicket, showModalChangeStatusTicket: true});
  };

  const handleCloseTicket = async () => {
    const ticketStatus = {
      id_ticket: ticket.id_ticket,
      nuovo_stato: STATO_TICKET.CHIUSO
    };
    await changeTicketStatus(ticketStatus);
    onClose();
    userLoggedIn.role === ROLES.CORSISTA ? navigate("/area-personale/ticket") : navigate("/gestione-ticket");
  };

  const handleViewEditTicket = () => {
    setFormUpdateTicket(ticket);
    setModalStateTicket({...modalStateTicket, showModalUpdateTicket: true});
  };

  const handleEditTicket = async () => {
    const obj = {
      id_ticket: ticket.id_ticket,
      oggetto: formUpdateTicket.oggetto,
      descrizione: formUpdateTicket.descrizione
    };
    await updateTicket(obj);
    onClose();
    fetchTicket();
  };
  
  const onClose = () => {
    setModalStateTicket({...modalStateTicket, showModalChangeStatusTicket: false, showModalUpdateTicket: false})
  }


  const handleReopenTicket = async () => {
    const ticketStatus = {
      id_ticket: ticket.id_ticket,
      nuovo_stato: STATO_TICKET.APERTO
    };
    await changeTicketStatus(ticketStatus);
    fetchTicket();
  };

  if (!ticket) {
    return <CardLoadingPage />;
  }

  return (
    <>
    <div className={`card custom-card w-100 shadow ${isWideScreen ? "p-5" : "px-3 py-4"}`}>
      <div className="container">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <ComebackArrow path={userLoggedIn.role === ROLES.CORSISTA ? "/area-personale/ticket" : "/gestione-ticket"} />
          <div>
            <b>Stato ticket: {" "}</b>
            <Badge bg={isTicketClosed ? "danger" : "success"} className="mt-2 fs-6">
              {isTicketClosed ? "Chiuso" : "Aperto"}
            </Badge>
          </div>
        </div>
        
        <div className={`${isMobile || isTablet ? 'flex-column' : 'd-flex justify-content-between'} align-items-start mb-4`}>
          <div className="mb-3 mb-md-0">
            <h2 className="mb-0">{ticket.oggetto}</h2>
          </div>
          <div className={`d-flex ${isMobile || isTablet ? 'mt-3' : ''}`}>
            {!isTicketClosed && (
              <>
              {
                (userLoggedIn.role !== ROLES.DOCENTE && userLoggedIn.role !== ROLES.TUTOR && userLoggedIn.role !== ROLES.COURSE_DESIGNER) && ( 
                  <IconWithTooltip 
                    icon="bi-pencil-square"
                    text="Modifica Ticket"
                    onClick={handleViewEditTicket}
                    variant="outline-primary"
                    className="me-2"
                  />
                )
              }
              </>
            )}
            {isTicketClosed ? (
              <>
              {
                userLoggedIn.role !== ROLES.CORSISTA && (
                  <IconWithTooltip 
                    icon="bi-arrow-counterclockwise"
                    text="Riapri Ticket"
                    onClick={handleReopenTicket}
                    variant="primary"
                    disabled={loadingStateTicket.isLoadingTicketChangeStatus}
                  />
                )
              }
              </>
            ) : (
              <IconWithTooltip 
                icon="bi-check-circle"
                text="Chiudi Ticket"
                onClick={handleViewCloseTicket}
                variant="success"
              />
            )}
          </div>
        </div>

        <div className="ticket-details bg-light p-4 rounded mb-4">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <strong>{ticket.utente.nome} {ticket.utente.cognome}</strong>
              <span className="badge bg-primary ms-2">{ticket.utente.tipo_utente}</span>
            </div>
            <small className="text-muted">
              {formatDateTime(ticket.data_inserimento)}
            </small>
          </div>
          <p className="mb-2">{ticket.descrizione}</p>
          <small className="text-muted">
            <i className="bi bi-book me-1"></i>
            Corso: {ticket?.classe?.denominazione_corso}
          </small>
        </div>

        {!isTicketClosed && (
          <button 
            className="btn btn-primary mb-3"
            onClick={() => setShowReplyForm(!showReplyForm)}
          >
            <i className={`bi ${showReplyForm ? 'bi-x' : 'bi-reply'} me-2`}></i>
            {showReplyForm ? 'Nascondi' : 'Rispondi al ticket'}
          </button>
        )}

        {showReplyForm && !isTicketClosed && (
          <div className="reply-form mb-4">
            <textarea 
              className="form-control mb-3" 
              rows="4" 
              placeholder="Scrivi la tua risposta..."
              value={ticketReplyText}
              onChange={(e) => setTicketReplyText(e.target.value)}
            ></textarea>
              <CustomButton 
                onClick={() => handleReply()} 
                isLoading={loadingStateTicketMessaggio.isLoadingTicketMessaggioCreating}
                disabled={!ticketReplyText.trim()}
                label="Invia risposta"
                icon="bi-send"
              />
          </div>
        )}

        <div className="ticket-replies">
          <h5 className="mb-3">
            <i className="bi bi-chat-dots me-2"></i>
            Risposte al ticket
          </h5>
          {ticketMessaggi?.length > 0 ? (
            <div className="ticket-replies-list">
              {renderPaginatedMessages()}
            </div>
          ) : (
              <p>Non ci sono messaggi da mostrare.</p>
          )}
          <div className="d-flex justify-content-end mt-3">
            <CustomPagination
              totalPages={totalPages}
              selectedPage={selectedPage}
              handlePageChange={handlePageChange}
            />
          </div>
        </div>
      </div>
    </div>

    <CustomModal
      show={modalStateTicket.showModalChangeStatusTicket}
      onHide={onClose}
      headerText="Chiudi ticket"
      onUndoChanges={onClose}
      isLoading={loadingStateTicket.isLoadingTicketChangeStatus}
      onSave={handleCloseTicket}
      btnText="Chiudi ticket"
      variant={"success"}
      body={<>Vuoi davvero chiudere il ticket?</>}
    />

    <CustomModal
      show={modalStateTicket.showModalUpdateTicket}
      onHide={onClose}
      headerText="Modifica ticket"
      size={"lg"}
      btnText={"Salva modifiche"}
      body={<TicketManagerForm 
          formTicketManager={formUpdateTicket}
          setFormTicketManager={setFormUpdateTicket}
          showModalTicketManager={modalStateTicket.showModalUpdateTicket}
          isUpdate={true}
        />} 
        isLoading={loadingStateTicket.isLoadingTicketUpdate}
        closeButton={true}
        onUndoChanges={onClose}
        onSave={handleEditTicket} 
      />

    </>
  );
}
